<script>
import { sync } from 'vuex-pathify'
const { ProductPromoType } = require('#/shop-catalog/services/product-promo-constants')
import formatPrice from '@/v-shop/helpers/format-price'

export default {
	lang: 'shop',
	props: {
		product: Object,
		variant: Object,
		promos: Array,
		customization: Object,
	},
	computed: {
		ctaIsLoading: sync('shop/ctaIsLoading'),
		outOfStock() {
			return this.variant?.stock.availability == 'OutOfStock'
		},
		price() {
			return this.variant?.pvPrice.price
		},
		currency() {
			return this.variant?.pvPrice.priceConfig.currency.sign
		},
		maxStockQty() {
			let { maxBuyableQty, infiniteQty, qty } = this.variant.stock
			if (maxBuyableQty >= 1) {
				if (infiniteQty) return maxBuyableQty
				else return Math.min(qty, maxBuyableQty)
			} else {
				if (infiniteQty) return 0
				else return qty
			}
		},
	},
	methods: {
		giftProduct(promo) {
			return promo.giftProductsByVariantId?.[this.variant?.id]
		},
		comboProducts(promo) {
			return Object.values(promo.comboProductsByVariantId?.[this.variant?.id] || {})
		},
		comboTotalPrice(promo) {
			const comboProds = this.comboProducts(promo)
			const comboSum = comboProds.reduce((sum, p) => sum + (Number(p.price) || 0), 0)
			return this.price + comboSum
		},
		async onCtaClick(qty) {
			return this.$shop.addToCart({
				productId: this.product.id,
				variantId: this.variant.id,
				qty,
				sum: true,
				customization: this.customization,
				loading: (v) => (this.ctaIsLoading = v),
				onValidation: ({ validation }) => this.$emit('validation', validation),
			})
		},
		getCtas(promo) {
			if (!promo.cta) return []
			const variantData = promo.variantsData.find((v) => v.id == this.variant.id)

			let ctas = []
			if (promo.type == ProductPromoType.BUY_X_PAY_Y) {
				ctas = promo.data.configs
					.map((config) => ({ ...config }))
					.sort((a, b) => a.x - b.x)
					.map((config, i) => {
						return {
							data: { ...config, unitPrice: (this.price * Number(config.y)) / Number(config.x) },
							addQty: Number(config.x) * Number(this.variant.metricFactor),
							inStock: !!variantData?.configsData?.[i]?.inStock,
						}
					})
			} else if (promo.type == ProductPromoType.SAMPLING) {
				ctas = [
					{
						data: {},
						addQty: Number(this.variant.metricFactor),
						inStock: !!variantData?.inStock,
					},
				]
			} else if (promo.type == ProductPromoType.DISCOUNT_ON_X) {
				ctas = [
					{
						data: {
							unitPrice:
								(this.price * Number(promo.data.x) -
									(this.price * Number(promo.data.discountPct)) / 100) /
								Number(promo.data.x),
						},
						addQty: Number(promo.data.x) * Number(this.variant.metricFactor),
						inStock: !!variantData?.inStock,
					},
				]
			} else if (promo.type == ProductPromoType.DISCOUNT_AFTER_X) {
				ctas = [
					{
						addQty: Number(promo.data.x) * Number(this.variant.metricFactor),
						inStock: !!variantData?.inStock,
					},
				]
			} else if (promo.type == ProductPromoType.COMBO) {
				ctas = [
					{
						data: {},
						addQty: 1,
					},
				]
			}

			const money = (amount, opts = {}) => {
				return (
					'<span class="nowrap">' +
					formatPrice(amount, {
						currency: this.currency,
						...opts,
					}) +
					'</span>'
				)
			}

			ctas.forEach((cta) => {
				let data = { ...promo.data, ...cta.data, money }
				cta.text = promo.cta ? this.evalPromoText(data, promo.cta) : null
				cta.subText = promo.subCta ? this.evalPromoText(data, promo.subCta) : null
				cta.priceInfo = this.priceInfo(cta, promo) || null
			})

			return ctas
		},
		formatPrice,
		priceInfo(cta, promo) {
			const price = this.price
			const qty = cta.addQty
			const originalTotal = price * qty

			if (promo.type == ProductPromoType.BUY_X_PAY_Y) {
				const finalTotal = price * cta.data.y
				return {
					originalTotal,
					finalTotal,
					discountPct: Math.round((1 - cta.data.y / qty) * 100),
				}
			}
			if (promo.type == ProductPromoType.DISCOUNT_ON_X) {
				const discPct = promo.data.discountPct
				const finalTotal = price * (qty - 1) + price * (1 - discPct / 100)
				return {
					originalTotal,
					finalTotal,
					discountPct: Math.round(discPct / qty),
				}
			}
			if (promo.type == ProductPromoType.COMBO) {
				const total = this.comboTotalPrice(promo)
				const discPct = Number(promo.data.discountPct) || 0
				return {
					originalTotal: total,
					finalTotal: total * (1 - discPct / 100),
					discountPct: discPct,
				}
			}
			return null
		},
		evalPromoText(fnVars, text) {
			try {
				return text
					.replace(/{{(.*?)}}/g, (_, expression) => {
						let fnStr = `
							let { ${Object.keys(fnVars).join(', ')} } = fnVars
							return ${expression}
						`
						let func = new Function('fnVars', fnStr)
						return func(fnVars)
					})
					.trim()
			} catch (error) {
				return ''
			}
		},
		getProductImageThumb(product) {
			return product?.images?.[0]?.thumb
		},
	},
}
</script>

<template>
	<div id="product-promos" class="d-flex flex-column" style="gap: 12px">
		<div
			v-for="promo in promos"
			:key="promo.id"
			class="promo-item pa-3 grey lighten-4 rounded d-flex flex-column"
			style="gap: 8px; border: 1px solid #bbb !important"
		>
			<div class="d-flex align-center" style="gap: 8px">
				<v-icon color="var(--cta)" size="28" class="flex-shrink-0">mdi-star</v-icon>
				<div>
					<div class="font-1 font-weight-bold">{{ promo.name }}</div>
					<div
						class="font-0 grey--text text--darken-2"
						v-html="promo.message"
						v-if="promo.message"
					></div>
				</div>
			</div>
			<SafeLink
				v-if="promo.type == 'sampling' && giftProduct(promo)"
				:to="{
					name: 'product',
					params: { urlName: giftProduct(promo).urlName, id: giftProduct(promo).id },
					query: { variant: giftProduct(promo).variantId },
				}"
			>
				<div class="d-flex align-center" style="gap: 8px">
					<Media
						v-if="giftProduct(promo).image"
						:src="giftProduct(promo).image"
						width="50"
						class="flex-shrink-0 rounded"
					/>
					<v-icon v-else color="grey" size="40" class="flex-shrink-0">mdi-package-variant</v-icon>
					<div>
						<div class="grey--text text--darken-1 font-weight-regular font-0">Producto de Regalo:</div>
						<div class="font-weight-bold font-1">{{ giftProduct(promo).name }}</div>
					</div>
				</div>
			</SafeLink>
			<div
				v-if="promo.type == 'combo' && comboProducts(promo).length"
				class="d-flex flex-column"
				style="gap: 6px"
			>
				<div class="font-0 grey--text text--darken-1 font-weight-regular mb-1">Productos incluidos:</div>
				<div class="d-flex align-center" style="gap: 8px">
					<Media
						v-if="getProductImageThumb(product)"
						:src="product.images[0].thumb"
						width="40"
						class="flex-shrink-0 rounded"
					/>
					<v-icon v-else color="grey" size="32" class="flex-shrink-0">mdi-package-variant</v-icon>
					<div class="font-1 font-weight-bold" style="line-height: 1.2">{{ product.name }}</div>
				</div>
				<div
					v-for="cp in comboProducts(promo)"
					:key="cp.sku"
					class="d-flex align-center"
					style="gap: 8px"
				>
					<SafeLink
						:to="{
							name: 'product',
							params: { urlName: cp.urlName, id: cp.id },
							query: { variant: cp.variantId },
						}"
						class="d-flex align-center"
						style="gap: 8px; text-decoration: none"
					>
						<Media v-if="cp.image" :src="cp.image" width="40" class="flex-shrink-0 rounded" />
						<v-icon v-else color="grey" size="32" class="flex-shrink-0">mdi-package-variant</v-icon>
						<div>
							<div class="font-1 font-weight-bold" style="line-height: 1.2">{{ cp.name }}</div>
							<div class="font-0 grey--text">{{ formatPrice(cp.price, { currency }) }}</div>
						</div>
					</SafeLink>
				</div>
			</div>
			<Button
				v-for="(cta, i) in getCtas(promo)"
				:key="i"
				class="w100"
				color="cta"
				:disabled="outOfStock || !product.buyable || !cta.inStock"
				:loading="ctaIsLoading"
				@click="onCtaClick(cta.addQty)"
				outlined
				style="height: unset; padding: 8px 12px"
			>
				<div>
					<div class="d-flex align-center justify-center">
						<v-icon class="font-0 mr-2">mdi-cart-outline</v-icon>
						<span class="font-2" v-html="cta.text"></span>
					</div>
					<div v-if="cta.subText" class="font-0" v-html="cta.subText"></div>
					<div
						v-if="cta.priceInfo"
						class="d-flex align-center justify-center flex-wrap mt-1"
						style="gap: 4px 8px"
					>
						<span class="grey--text" style="text-decoration: line-through; font-size: 0.9rem">
							{{ formatPrice(cta.priceInfo.originalTotal, { currency }) }}
						</span>
						<span class="font-weight-bold" style="font-size: 1.1rem">
							{{ formatPrice(cta.priceInfo.finalTotal, { currency }) }}
						</span>
						<span
							class="secondary white--text px-2 rounded font-weight-bold"
							style="font-size: 0.8rem; line-height: 1.6"
						>
							-{{ cta.priceInfo.discountPct }}%
						</span>
					</div>
				</div>
			</Button>
		</div>
	</div>
</template>

